import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  insertSelectedItems,
  removeSelectedItems,
} from '@disco/state-management/actions/ShortlistActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import {
  getShortlistsSelectedItems,
  makeGetSelectionIndexes,
} from '@disco/state-management/selectors/selection'
import {
  getVisibleShortlistIds,
  makeGetShortlistTracks,
  shortlistHasActiveUploads,
} from '@disco/state-management/selectors/shortlists'

import { getShortlistBaseRoute } from 'utils/routing'

import { deleteShortlist } from '@disco/state-management/actions/shortlist-upload-actions'
import { AccessibleIcon, DropdownMenu, IconButton } from 'components/ds'
import { EllipsisVerticalIcon } from 'components/ui/icon/svg-icons'
import { RxCross2 } from '@react-icons/all-files/rx/RxCross2'
import { RxEraser } from '@react-icons/all-files/rx/RxEraser'
import { RxMinus } from '@react-icons/all-files/rx/RxMinus'
import { RxPencil2 } from '@react-icons/all-files/rx/RxPencil2'
import { RxPlusCircled } from '@react-icons/all-files/rx/RxPlusCircled'
import { RxMixerVertical } from '@react-icons/all-files/rx/RxMixerVertical'

import styles from './shortlist-menu.module.css'

function makeMapStateToProps() {
  const getSelectionIndexes = makeGetSelectionIndexes()
  const getShortlistTracks = makeGetShortlistTracks()
  return (state, props) => {
    const selectedItems = getShortlistsSelectedItems(state)
    return {
      shortlistIds: getVisibleShortlistIds(state),
      shortlistTracks: getShortlistTracks(state, props),
      selectedIndexes: getSelectionIndexes(state, props.shortlistId),
      selectedItems,
      selectedItemsWithinUserTerritories: selectedItems.filter(
        track => !track.outside_your_territories,
      ),
      isPhone: isDeviceTypePhone(state),
      location: getLocationBeforeTransitions(state),
      isSaved: state.shortlists.byId[props.shortlistId]?.saved ?? false,
      activeUploads: shortlistHasActiveUploads(state, {
        id: props.shortlistId,
      }),
    }
  }
}

const mapDispatchToProps = {
  insertSelectedItems,
  removeSelectedItems,
  sendMixpanelEvent,
  deleteShortlist,
}

const connector = connect(makeMapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  disabled?: boolean
  shortlistId: string
  navigate: NavigateFunction
  toggleShortlistActive: (...args: Array<any>) => any
}

function ShortlistMenu({
  disabled,
  shortlistId,
  navigate,
  toggleShortlistActive,
  shortlistTracks,
  isPhone,
  location,
  sendMixpanelEvent,
  deleteShortlist,
  isSaved,
  activeUploads,
}: Props) {
  const baseRoute = getShortlistBaseRoute(location)
  const shortlistContainsNotProcessedTracks =
    shortlistTracks.filter(track => !track.uploaded).length > 0
  const editAllDisabled =
    shortlistTracks.filter(track => !track.outside_your_territories).length ===
      0 || shortlistContainsNotProcessedTracks
  const trackOrderDisabled =
    shortlistTracks.length === 0 || shortlistContainsNotProcessedTracks

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton
          variant="ghost"
          color="gray"
          size="2"
          disabled={disabled}
          data-testid="shortlist-menu-trigger"
        >
          <AccessibleIcon label="Playlist actions">
            <EllipsisVerticalIcon size={16} />
          </AccessibleIcon>
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className={styles.content}>
        <DropdownMenu.Item
          onClick={() => {
            sendMixpanelEvent('Playlist Creator - Close')
            if (shortlistTracks.length === 0 || (isSaved && !activeUploads)) {
              deleteShortlist(shortlistId)
            } else {
              navigate(`${baseRoute}/shortlists/${shortlistId}/close`)
            }
          }}
        >
          <RxCross2 />
          Close
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onClick={() => {
            sendMixpanelEvent('Playlist Creator - Clear')
            navigate(`${baseRoute}/shortlists/${shortlistId}/delete`)
          }}
        >
          <RxEraser />
          Clear
        </DropdownMenu.Item>
        {!isPhone && (
          <DropdownMenu.Item
            onClick={() => {
              sendMixpanelEvent('Playlist Creator - Minimise')
              toggleShortlistActive()
            }}
          >
            <RxMinus />
            Minimise
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Separator />
        {!isPhone && (
          <DropdownMenu.Item
            disabled={editAllDisabled}
            onClick={() => {
              sendMixpanelEvent('Playlist Creator - Edit all')
              navigate(`${baseRoute}/shortlists/${shortlistId}/bulk-edit?all=1`)
            }}
          >
            <RxPencil2 />
            Edit all
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item
          onClick={() => {
            sendMixpanelEvent('Playlist Creator - Add section')
            navigate(`${baseRoute}/shortlists/${shortlistId}/sections/add`)
          }}
        >
          <RxPlusCircled />
          Add section
        </DropdownMenu.Item>
        {!isPhone && (
          <DropdownMenu.Item
            disabled={trackOrderDisabled}
            onClick={() => {
              sendMixpanelEvent('Playlist Creator - Change track order')
              navigate(`${baseRoute}/shortlists/${shortlistId}/track-order`)
            }}
          >
            <RxMixerVertical />
            Change track order
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

type OwnProps = {
  disabled?: boolean
  shortlistId: string
  toggleShortlistActive: (...args: Array<any>) => any
}

const ConnectedShortlistMenu = connector(ShortlistMenu)

export default function ShortlistMenuWithRouter(props: OwnProps) {
  const navigate = useNavigate()
  return <ConnectedShortlistMenu {...props} navigate={navigate} />
}
